// SSR-safe facade for product analytics (PostHog). Components, store modules
// and utilities call these functions; they never import posthog-js. The
// client-only loader (posthog.ts, imported from entry-client.js) installs
// window.__vfPh and drains the queue once PostHog is allowed to run, or sets
// window.__vfPhDisabled when it will not run on this page.

export type PhProps = Record<string, unknown>;

export type PhCustomer = {
  id: string | number;
  email?: string | null;
};

export type PhQueueEntry =
  | { type: "track"; event: string; props?: PhProps }
  | { type: "identify"; customer: PhCustomer }
  | { type: "guestEmail"; email: string }
  | { type: "reset" };

export type PhAdapter = {
  track: (event: string, props?: PhProps) => void;
  identify: (customer: PhCustomer) => void;
  reset: () => void;
  setGuestEmail: (email: string) => void;
  optOut: () => void;
};

declare global {
  interface Window {
    __vfPh?: PhAdapter;
    __vfPhQueue?: PhQueueEntry[];
    __vfPhDisabled?: boolean;
  }
}

export const PH_QUEUE_LIMIT = 50;

const enqueue = (entry: PhQueueEntry): void => {
  if (window.__vfPhDisabled) return;
  const queue = (window.__vfPhQueue = window.__vfPhQueue || []);
  queue.push(entry);
  while (queue.length > PH_QUEUE_LIMIT) {
    // Drop the oldest tracking entry first so identity changes keep their
    // order relative to the events that remain.
    const index = queue.findIndex((item) => item.type === "track");
    queue.splice(index >= 0 ? index : 0, 1);
  }
};

const dispatch = (
  call: (adapter: PhAdapter) => void,
  entry: PhQueueEntry
): void => {
  if (typeof window === "undefined") return;
  try {
    if (window.__vfPh) call(window.__vfPh);
    else enqueue(entry);
  } catch {
    // Analytics must never interrupt the storefront.
  }
};

export function phTrack(event: string, props?: PhProps): void {
  if (!event) return;
  dispatch((adapter) => adapter.track(event, props), {
    type: "track",
    event,
    props,
  });
}

export function phIdentifyCustomer(customer: PhCustomer): void {
  if (!customer) return;
  dispatch((adapter) => adapter.identify(customer), {
    type: "identify",
    customer,
  });
}

export function phResetCustomer(): void {
  dispatch((adapter) => adapter.reset(), { type: "reset" });
}

// Owner D5b: the order email of a guest buyer (no customer account signed in)
// goes onto this browser's anonymous PostHog profile. The adapter checks the
// address shape and that no customer is identified on this browser.
export function phSetGuestEmail(email: string | null | undefined): void {
  if (typeof email !== "string" || !email.trim()) return;
  dispatch((adapter) => adapter.setGuestEmail(email), {
    type: "guestEmail",
    email,
  });
}
